
import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Database, Layers, Zap } from 'lucide-react';
import AnimatedCard from '@/components/ui/AnimatedCard';
import SmartMeterHealthSummary from '@/components/SmartMeterHealthSummary';
import IoTGatewayHealthSummary from '@/components/IoTGatewayHealthSummary';
import VoltageRegulatorSummaryStats from './VoltageRegulatorSummaryStats';
import VoltageRegulatorTable from './VoltageRegulatorTable';

interface DeviceOverviewTabProps {
  filteredSmartMeters: any[];
  filteredGateways: any[];
  filteredVoltageRegulators?: any[];
  isLoading: boolean;
  onSelectDevice: (deviceId: string, deviceType: 'smartMeter' | 'gateway' | 'voltageRegulator') => void;
  selectedFilter: string | null;
  onFilterSelect: (filter: string | null) => void;
}

const DeviceOverviewTab = ({
  filteredSmartMeters,
  filteredGateways,
  filteredVoltageRegulators = [],
  isLoading,
  onSelectDevice,
  selectedFilter,
  onFilterSelect
}: DeviceOverviewTabProps) => {
  const [activeSubTab, setActiveSubTab] = useState("smartMeters");

  return (
    <div className="space-y-4 w-full h-full overflow-hidden flex flex-col">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full h-full flex flex-col">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="smartMeters" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            <span>Smart Meter Health</span>
          </TabsTrigger>
          <TabsTrigger value="gateways" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            <span>IoT Gateway Health</span>
          </TabsTrigger>
          <TabsTrigger value="voltageRegulators" className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            <span>Voltage Regulators</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="smartMeters" className="space-y-4 flex-grow overflow-auto">
          <AnimatedCard
            animation="fade-up"
            delay={150}
            className="border-none shadow-sm"
          >
            <SmartMeterHealthSummary 
              smartMeters={filteredSmartMeters} 
              isLoading={isLoading} 
              onSelectDevice={(deviceId) => onSelectDevice(deviceId, 'smartMeter')}
              selectedFilter={selectedFilter}
              onFilterSelect={onFilterSelect}
            />
          </AnimatedCard>
        </TabsContent>

        <TabsContent value="gateways" className="space-y-4 flex-grow overflow-auto">
          <AnimatedCard
            animation="fade-up"
            delay={150}
            className="border-none shadow-sm"
          >
            <IoTGatewayHealthSummary 
              gateways={filteredGateways} 
              isLoading={isLoading}
              onSelectDevice={(deviceId) => onSelectDevice(deviceId, 'gateway')}
              selectedFilter={selectedFilter}
              onFilterSelect={onFilterSelect}
            />
          </AnimatedCard>
        </TabsContent>

        <TabsContent value="voltageRegulators" className="space-y-4 flex-grow overflow-auto">
          <AnimatedCard
            animation="fade-up"
            delay={150}
            className="border-none shadow-sm"
          >
            <VoltageRegulatorSummaryStats 
              regulators={filteredVoltageRegulators} 
              isLoading={isLoading}
            />
          </AnimatedCard>
          
          <AnimatedCard
            animation="fade-up"
            delay={300}
            className="border-none shadow-sm"
          >
            <VoltageRegulatorTable 
              regulators={filteredVoltageRegulators} 
              onSelectDevice={(deviceId) => onSelectDevice(deviceId, 'voltageRegulator')}
            />
          </AnimatedCard>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DeviceOverviewTab;
